import React, { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { TestimonialCard } from "@/.storyblok/types/319751/storyblok-components";

interface TestimonialProps {
  testimonials: TestimonialCard[];
  hideHeading?: boolean;
  heading?: string;
}

const Testimonial: React.FC<TestimonialProps> = ({
  testimonials,
  hideHeading = false,
  heading = "Here's what our customers say about us"
}) => {

  useEffect(() => {
    let carouselInstance: any;

    const initCarousel = async () => {
      if (typeof window !== 'undefined') {
        try {
          const bootstrap = await import('bootstrap');
          const carouselElement = document.getElementById('carouselTestimonial');
          if (carouselElement) {
            carouselInstance = new bootstrap.Carousel(carouselElement);
          }
        } catch (error) {
          console.error('Error initializing carousel:', error);
        }
      }
    };

    initCarousel();

    return () => {
      if (carouselInstance?.dispose) {
        carouselInstance.dispose();
      }
    };
  }, []);



  if (!testimonials?.length) {
    return null;
  }

  const validTestimonials = testimonials;

  if (!validTestimonials.length) {
    return null;
  }

  const hasMultipleTestimonials = validTestimonials.length > 1;

  return (
    <>
      {!hideHeading && (
        <h2 className="text-primary text-center mb-4">{heading}</h2>
      )}

      <div
        id="carouselTestimonial"
        className="carousel carousel-testimonial slide d-flex flex-column js-carousel-animate-height"
        data-bs-ride="carousel"
        data-bs-interval="10000"
      >
        {hasMultipleTestimonials && (
          <>
            <button
              className="carousel-control-prev rounded-circle lh-1 d-none d-lg-flex my-auto"
              type="button"
              data-bs-target="#carouselTestimonial"
              data-bs-slide="prev"
            >
              <i className="fas fa-chevron-left carousel-control-icon" />
              <span className="visually-hidden">Previous</span>
            </button>
            <button
              className="carousel-control-next rounded-circle lh-1 d-none d-lg-flex my-auto"
              type="button"
              data-bs-target="#carouselTestimonial"
              data-bs-slide="next"
            >
              <i className="fas fa-chevron-right carousel-control-icon" />
              <span className="visually-hidden">Next</span>
            </button>
          </>
        )}

        <div className="carousel-inner">
          {validTestimonials.map((testimonial, index) => {
            const {
              _uid,
              name,
              position,
              photo,
              company,
              logo,
              copy,
              url,
              cta_button,
              cta_button_link
            } = testimonial;

            return (
              <div
                key={_uid || `testimonial-${index}`}
                className={`carousel-item ${index === 0 ? "active" : ""}`}
              >
                <div className="row justify-content-center">
                  <div className="col-lg-18 col-xl-14">
                    <blockquote className="unstyled text-center mb-3 mb-md-4">
                      {copy}
                    </blockquote>
                  </div>
                </div>

                <div className="d-flex flex-column flex-md-row justify-content-center align-items-center">
                  {logo?.filename && (
                    <div className={url?.cached_url ? 'cursor-pointer' : ''}>
                      {url?.cached_url ? (
                        <Link
                          href={url.cached_url}
                          target="_blank"
                          rel="noopener"
                        >
                          <Image
                            src={logo.filename}
                            alt={`${company || 'Company'} logo`}
                            width={236}
                            height={61}
                            className="js-lazy-load testimonial-logo"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            loading="lazy"
                            quality={75}
                          />
                        </Link>
                      ) : (
                        <Image
                          src={logo.filename}
                          alt={`${company || 'Company'} logo`}
                          width={236}
                          height={61}
                          className="js-lazy-load testimonial-logo"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          loading="lazy"
                          quality={75}
                        />
                      )}
                    </div>
                  )}

                  {/* Mobile view */}
                  <div className="text-center mt-3 d-md-none">
                    {photo?.filename && (
                      <Image
                        src={photo.filename}
                        alt={name || ""}
                        width={60}
                        height={60}
                        className="js-lazy-load mb-2"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        loading="lazy"
                        quality={75}
                      />
                    )}
                    <div className="lead fw-bold text-dark mb-0">{name}</div>
                    {position && <div className="text-muted">{position}</div>}
                    {company && <div className="text-muted">{company}</div>}
                  </div>

                  {/* Desktop view */}
                  <div className="ms-md-4 d-none d-md-flex align-items-center">
                    {photo?.filename && (
                      <Image
                        src={photo.filename}
                        alt={name || ""}
                        width={60}
                        height={60}
                        className="js-lazy-load me-3"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        loading="lazy"
                        quality={75}
                      />
                    )}
                    <div>
                      <div className="lead fw-bold text-dark mb-0">{name}</div>
                      {(company || position) && (
                        <div className="text-muted lead">
                          {[company, position].filter(Boolean).join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {cta_button && cta_button_link?.cached_url && (
                  <div className="d-flex justify-content-center mt-4">
                    <Link
                      className="btn btn-outline-primary"
                      target="_blank"
                      href={cta_button_link.cached_url}
                      rel="noopener"
                    >
                      {cta_button}
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {hasMultipleTestimonials && (
          <div className="carousel-indicators position-relative mt-4 mb-0">
            {validTestimonials.map((_, index) => (
              <button
                key={`indicator-${index}`}
                type="button"
                data-bs-target="#carouselTestimonial"
                data-bs-slide-to={index}
                className={`carousel-indicator rounded-circle border-top-0 border-bottom-0 ${
                  index === 0 ? "active" : ""
                }`}
                aria-current={index === 0 ? "true" : undefined}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default Testimonial;